import React, { useEffect, useMemo, useState } from 'react';
import {
  Input,
  Form,
  Modal,
  Select,
  message,
  notification,
  Col,
  Row,
  Divider,
} from 'antd';
import { Button } from 'components/gyramais';
import './styles.less';
import { useMutation, useQuery } from '@apollo/client';
import { formatError } from 'utils';
import UploadCard from 'components/cards/UploadCard';
import {
  dfAuditada,
  tamanhoEmpresa, tipoAnalise, tipoContrato, tipoFornecedor,
  variableOptions,
  variables,
} from './options';

import SAVE_VOLTZ_CONTRACT_DATA from './graphql/saveVoltzContractData.gql';
import LOAN from './graphql/loan.gql';
import BUSINESS from './graphql/business.gql';
import ADD_ADDITIONAL_FILE from './graphql/addAdditionalFile.gql';
import SAVE_LOAN_EVENT from './graphql/saveLoanEvent.gql';

const { Item, useForm } = Form;
const { Option } = Select;

const inputs = [
  // Está hard-coded por ser um campo que controla outros campos
  // {
  //   label: 'Tipo de fornecedor',
  //   value: 'tipoFornecedor',
  //   options: tipoFornecedor,
  // },
  {
    label: 'Tamanho da empresa',
    value: 'tamanhoEmpresa',
    options: tamanhoEmpresa,
    showIf: (values = {}) => !!values.tipoFornecedor,
  },
  {
    label: 'Tipo de contrato',
    value: 'tipoContrato',
    options: tipoContrato,
    showIf: (values = {}) => values.tipoFornecedor === 'Serviços',
  },
  {
    label: 'Tipo de análise',
    value: 'tipoAnalise',
    options: tipoAnalise,
    showIf: (values = {}) => !!values.tipoFornecedor,
  },
  {
    label: 'Demonstrativo financeiro auditado',
    value: 'dfAuditada',
    options: dfAuditada,
    showIf: (values = {}) => values.tamanhoEmpresa === 'G',
  },
];

const VoltzContractData = ({
  visible, loanId, businessId, onClose, refetch: parentRefetch,
}) => {
  const [form] = useForm();
  const [contractValues, setContractValues] = useState({});

  const contractKey = useMemo(() => `${contractValues.tipoFornecedor || ''}:${contractValues.tamanhoEmpresa || ''}:${contractValues.tipoContrato || ''}:${contractValues.tipoAnalise || ''}`, [contractValues]);
  const contractVariables = useMemo(() => variables[contractKey], [contractKey]);

  const handleClose = () => {
    onClose();
    form.resetFields();
    setContractValues({});
  };

  const {
    data: { business } = {},
    loading: businessLoading,
    refetch,
  } = useQuery(BUSINESS, {
    variables: { id: businessId },
    skip: !businessId,
  });
  const {
    data: {
      loan: {
        additionalInfo,
      } = {},
    } = {},
  } = useQuery(LOAN, {
    variables: { id: loanId },
    skip: !loanId,
  });

  const [saveLoanEvent, { loading: saveLoanLoading }] = useMutation(SAVE_LOAN_EVENT, {
    onCompleted: async () => {
      notification.success({ message: 'Proposta atualizada com sucesso', duration: 10 });
      parentRefetch();
      handleClose();
    },
    onError: (error) => notification.error({ message: formatError(error), duration: 10 }),
  });

  const mappedAdditionalFiles = useMemo(() => {
    const mapped = business?.additionalFiles?.reduce((acc, file) => {
      if (!file.parent?.name) {
        return acc;
      }

      if (!acc[file.parent.name]) {
        acc[file.parent.name] = [];
      }

      acc[file.parent.name].push(file);
      return acc;
    }, {});

    if (mapped) {
      Object.keys(mapped)
        .filter((key) => key.includes('evidence-'))
        .forEach((key) => {
          const files = mapped[key];
          if (files.length === 0) {
            form.resetFields([key]);
          }

          form.setFieldValue(
            key,
            files.map((file) => file.id),
          );
        });
    }

    return mapped;
  }, [business]);

  const [addAdditionalFile] = useMutation(ADD_ADDITIONAL_FILE, {
    onCompleted: () => {
      notification.success({
        message: 'Arquivo adicionado com sucesso',
        duration: 10,
      });
    },
    onError: (error) => notification.error({ message: formatError(error), duration: 10 }),
  });
  const [saveVoltzContractData, { loading: saveLoading }] = useMutation(
    SAVE_VOLTZ_CONTRACT_DATA,
    {
      onCompleted: () => {
        saveLoanEvent({
          variables: {
            loanId,
            statusValue: 'verifying',
          },
        });
      },
      onError: (error) => {
        message(`Erro: ${error.message}`);
      },
    },
  );

  const handleSubmit = async () => {
    const values = form.getFieldsValue();
    const { requestedAmount } = business.additionalInfo;

    let error = '';

    const contrato = {
      tipoFornecedor: values.tipoFornecedor,
      prazo: 36,
      valorSolicitado: requestedAmount,
    };
    for (const input of inputs) {
      const value = values[input.value];
      if (value) {
        contrato[input.value] = value;
      }
    }

    const matriz = {};
    for (const { value: key, label } of contractVariables) {
      const evidences = mappedAdditionalFiles[`evidence-${key}`] || [];
      if (evidences.length === 0) {
        error = `Envie pelo menos 1 evidência de ${label}`;
        break;
      }

      matriz[key] = values[key];
    }

    if (error) {
      notification.error({ message: error });
      return;
    }

    saveVoltzContractData({
      variables: {
        loanId,
        contrato: JSON.stringify(contrato),
        matriz: JSON.stringify(matriz),
      },
    });
  };

  useEffect(() => {
    if (!visible) {
      return;
    }
    if (!additionalInfo) {
      return;
    }

    let { contratoVoltz, matrizVoltz } = additionalInfo || {};
    if (!contratoVoltz && !matrizVoltz) {
      return;
    }

    contratoVoltz = JSON.parse(contratoVoltz);
    matrizVoltz = JSON.parse(matrizVoltz);
    matrizVoltz = Object
      .keys(matrizVoltz)
      .reduce((acc, key) => {
        if (!matrizVoltz[key]) {
          return acc;
        }

        acc[key] = `${matrizVoltz[key]}`;
        return acc;
      }, {});

    setContractValues(contratoVoltz);
    form.setFieldsValue({ ...contratoVoltz, ...matrizVoltz });
  }, [additionalInfo, visible]);

  const loading = businessLoading || saveLoading;

  return (
    <Modal
      visible={Boolean(visible)}
      closable={false}
      centered
      title="Adicionar dados do contrato"
      confirmLoading={loading}
      cancelText="Cancelar"
      onCancel={handleClose}
      footer={(
        <>
          <Button color="white" onClick={handleClose} loading={loading || saveLoanLoading}>
            Fechar
          </Button>
          <Button onClick={form.submit} loading={loading || saveLoanLoading}>
            Salvar
          </Button>
        </>
      )}
    >
      <Form
        layout="vertical"
        onFinish={handleSubmit}
        form={form}
        className="voltz-contract-data-form"
      >
        <Row gutter={[0, 10]}>
          <Col xs={24} sm={24} md={12} lg={8}>
            <Item
              label="Tipo de fornecedor"
              name="tipoFornecedor"
              rules={[
                {
                  required: true,
                  message: 'Por favor, preencha o campo.',
                },
              ]}
              key="tipoFornecedor"
            >
              <Select
                placeholder="Selecione uma opção"
                onChange={() => {
                  const values = form.getFieldsValue();
                  const temp = Object.keys(values).reduce((acc, key) => {
                    if (key !== 'tipoFornecedor') {
                      acc[key] = undefined;
                      return acc;
                    }

                    acc[key] = values[key];
                    return acc;
                  }, {});

                  setContractValues(temp);
                  form.setFieldsValue(temp);
                }}
              >
                {tipoFornecedor.map((option) => (
                  <Option key={option} value={option}>
                    {option}
                  </Option>
                ))}
              </Select>
            </Item>
          </Col>

          <Col xs={0} sm={0} md={12} lg={16} />

          {inputs
            .filter((input) => (input.showIf ? input.showIf(contractValues) : true))
            .map((input) => (
              <Col xs={24} sm={24} md={12} lg={8}>
                <Item
                  label={input.label}
                  name={input.value}
                  rules={[
                    {
                      required: true,
                      message: 'Por favor, preencha o campo.',
                    },
                  ]}
                  key={input.value}
                >
                  {input.options ? (
                    <Select placeholder="Selecione uma opção" onChange={() => setContractValues(form.getFieldsValue())}>
                      {input.options.map((option) => (
                        <Option key={option} value={option}>
                          {option}
                        </Option>
                      ))}
                    </Select>
                  ) : (
                    <Input />
                  )}
                </Item>
              </Col>
            ))}
        </Row>

        {contractVariables && (
          <>
            <Divider />

            <Row gutter={[10, 10]}>
              {contractVariables
                .map(({ label, value: key }) => (
                  <Col xs={24} sm={24} md={24} lg={24} key={key}>
                    <Item
                      label={label}
                      name={key}
                      rules={[
                        {
                          required: true,
                          message: 'Por favor, preencha o campo.',
                        },
                      ]}
                      key={key}
                    >
                      {variableOptions[contractValues.tipoFornecedor][key] && (
                        <Select placeholder="Selecione uma opção" onChange={() => setContractValues(form.getFieldsValue())}>
                          {Object
                            .keys(variableOptions[contractValues.tipoFornecedor][key])
                            .flatMap((subKey) => {
                              // eslint-disable-next-line max-len
                              const value = variableOptions[contractValues.tipoFornecedor][key][subKey];
                              if (typeof value !== 'string') {
                                return Object.keys(value).map((litteKey) => (
                                  <Option key={subKey} value={subKey}>
                                    {value[litteKey][contractValues[litteKey]]}
                                  </Option>
                                ));
                              }

                              return [(
                                <Option key={subKey} value={subKey}>
                                  {value}
                                </Option>
                              )];
                            })}
                        </Select>
                      )}
                    </Item>

                    <UploadCard
                      id={`evidence-${key}-upload`}
                      accept="image/png,image/jpeg,application/pdf"
                      notSetFile
                      label={`Evidência de ${label}`}
                      multiples
                      actualFile={
                        mappedAdditionalFiles?.[`evidence-${key}`] || []
                      }
                      onDelete={() => {
                        refetch({ id: businessId });
                      }}
                      onCompleted={async ({ id: fileId }) => {
                        await addAdditionalFile({
                          variables: {
                            id: fileId,
                            businessId,
                            name: `evidence-${key}`,
                            multiple: true,
                          },
                        });
                        refetch({ id: businessId });
                      }}
                    />
                  </Col>
                ))}
            </Row>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default VoltzContractData;
